import { useAuth0 } from "@auth0/auth0-react";
import { Box, Text, Loader, Stack, Flex, Tooltip, Menu, Button, TextInput, ActionIcon, Badge } from "@mantine/core";
import { openConfirmModal } from "@mantine/modals";
import {
  useDebouncedValue,
  useDisclosure,
  useElementSize,
} from "@mantine/hooks";
import { IconCheck, IconSettings, IconSettings as IconSettingsMenu, IconCopy, IconX, IconTrash } from "@tabler/icons";
import { useContext, useEffect, useState, useMemo, useCallback, useRef } from "react";
import { canEditTeamIds, editSubpage, cloneSubpage, addViews, deleteSubpage } from "../../Api";
import { buildQueryString, loadListMembers, newBuildQueryString, getMergedColumns } from "./ideasHelpers";

import { getFixedDateRanges } from "./filters/FixedDateRanges";
import { noTrendFilterSpec } from "./filters/FiltersSpec";
import _ from "lodash";
import { showNotification } from "@mantine/notifications";
import SyncContext from "../../context/SyncContext";
import Header from "./components/Header";
import FilterActions from "./components/FilterActions";
import { useIdeas } from "../../hooks/useIdeas";
import FiltersContext, {
  FiltersContextProvider,
} from "./filters/context/FiltersContext";
import { useNavigate } from "react-router-dom";
import IdeasTable from "./Table/IdeasTable";
import { BgColorRouteContext } from "../../context/BgColorContext";
import { useLocation } from "react-router-dom";
import { EditTableViewModal } from "./EditTableViewModal";
import { getConnectedUserRole, isBagelAdmin } from "../../utils";
import { DataTableSortStatus } from "mantine-datatable";

const PAGE_SIZE = 50;

export const Ideas = () => {

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isArchivedRoute = location.pathname === "/ideas/archived";
  const { ref } = useElementSize();
  const { ref: warpRef, width: warpWidth } = useElementSize();
  const { ref: actionIconsRef, width: actionIconsWidth } = useElementSize();

  const syncContextData = useContext(SyncContext);
  const { ideasFiltersConfig, subPages, ideaColumns: fallbackColumns, fields, ideaDefaultSortColumn } = syncContextData;
  console.log('🔍 ideaDefaultSortColumn:', ideaDefaultSortColumn);
  
  const { activeFilters, setActiveFilters, setCheckedComponents, subPageKey, setSubPageKey, editMode, setEditMode } =
    useContext(FiltersContext);

  const [selectedView, setSelectedView] = useState<any>(null);
  const [search, setSearch] = useState<string>(searchParams.get("search") || "");
  const [page, setPage] = useState<string>(searchParams.get("page") || "1");
  const [isInitializing, setIsInitializing] = useState(true);
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus>(() => {
    // Try to get sortStatus from URL first
    const urlSortStatus = searchParams.get("sortStatus");
    console.log('🔍 URL sortStatus:', urlSortStatus);
    
    if (urlSortStatus) {
      try {
        const parsedSort = JSON.parse(urlSortStatus);
        if (parsedSort?.columnAccessor && parsedSort.columnAccessor !== "null" && parsedSort.columnAccessor !== null) {
          console.log('✅ Using URL sortStatus:', parsedSort);
          return parsedSort;
        }
      } catch (error) {
        console.error("Invalid JSON in sortStatus:", error);
      }
    }
    
    // Check for subpage-specific default sorting first
    if (subPageKey) {
      const subPage = subPages.find((page: any) => page.key === subPageKey);
      if (subPage?.defaultSortColumn) {
        console.log('✅ Using subpage default sort:', subPage.defaultSortColumn);
        return { columnAccessor: subPage.defaultSortColumn, direction: "asc" };
      }
    }
    
    // Fallback to global default sort column from configuration
    if (ideaDefaultSortColumn) {
      console.log('✅ Using global default sort:', ideaDefaultSortColumn);
      return { columnAccessor: ideaDefaultSortColumn, direction: "asc" };
    }
    
    // Final fallback to empty sort
    console.log('⚠️ No default sort found, using empty sort');
    return { columnAccessor: "", direction: "asc" };
  });
  console.log('🔍 Initial sortStatus:', sortStatus);
  const [isArchived, setIsArchived] = useState(isArchivedRoute);

  // Get ideas data using our custom hook
  const {
    ideas,
    loading,
    error,
    totalCount,
    refetch,
    starIdea,
    views,
    viewsLoading,
    upsertView,
    columns: apiColumns,
    columnsLoading,
    clearCache,
    invalidateFilters,
    invalidateSubPageCache,
    getCacheKey,
    subPage
  } = useIdeas({
    page,
    search,
    activeFilters: activeFilters,
    sortStatus,
    isArchived,
    selectedView,
    subPageKey,
    pathname: location.pathname
  });
  console.log('🔍 sortStatus passed to useIdeas:', sortStatus);
  const [savedViews, setSavedViews] = useState<any>(views || []);


  // Use API columns if available, otherwise fallback to SyncContext columns
  const ideaColumns = useMemo(() => {
    if (apiColumns && apiColumns.length > 0) {
      return apiColumns;
    }
    // Fallback to SyncContext columns while API loads or if it fails
    // return fallbackColumns || [];
  }, [apiColumns]);
  // Track initialized subpages to prevent redundant initializations
  // const initializedSubPages = useRef<Set<string>>(new Set());

  // Debug the component data
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 Ideas Component Debug:', {
      apiColumns: apiColumns.length ,
      fallbackColumns: fallbackColumns?.length || 0,
      finalIdeaColumns: ideaColumns?.length || 0,
      columnsLoading,
      subPage: subPage,
      subPageKey,
      location: location.pathname,
      search,
      loading,
      cacheKey: getCacheKey()
    });
  }

  // Invalidate cache when subPageKey changes (for debugging purposes)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && subPageKey) {
      console.log('🔄 SubPageKey changed to:', subPageKey, '- cache should auto-invalidate');
    }
  }, [subPageKey]);

  // Utility function for manual cache management (development only)
  const handleClearCache = useCallback(() => {
    if (process.env.NODE_ENV === 'development') {
      clearCache();
    }
  }, [clearCache]);

  const handleInvalidateSubPageCache = useCallback(() => {
    if (process.env.NODE_ENV === 'development') {
      invalidateSubPageCache();
    }
  }, [invalidateSubPageCache]);

  useEffect(() => {
    setIsArchived(isArchivedRoute);
  }, [isArchivedRoute]);

  const subPageName = subPage?.label;

  const auth0 = useAuth0();
  const navigate = useNavigate();

  const [debouncedSearch] = useDebouncedValue(search, 1000);
  const [loadingCols, setLoadingCols] = useState(false);
  const [searchOpened, setSearchOpened] = useState(false);
  const [isAllowedToEditDates, setIsAllowedToEditDates] = useState<boolean>(true);
  const [opened, { open, close: doClose }] = useDisclosure(false);
  const [viewName, setViewName] = useState("");
  const [menuFiltersObj, setMenuFiltersObj] = useState<any>(noTrendFilterSpec);
  const [filterRowOpened, { toggle }] = useDisclosure(true);
  const { setBgColor, setPageName } = useContext(BgColorRouteContext);
  const [isGlobal, setIsGlobal] = useState(selectedView?.isGlobal);
  const [isSubPageView, setIsSubPageView] = useState(selectedView?.isSubPageView);
  const [editTitle, setEditTitle] = useState(false);
  const [isTagsHovered, setIsTagsHovered] = useState<boolean>(false);
  const [sortState, setSortState] = useState<string>("total"); // For dynamic/bar cells

  const [inputValue, setInputValue] = useState(subPageName || 'ideas');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const userRole = getConnectedUserRole(auth0);
  // Initialize selectedView when views are loaded
  useEffect(() => {
    const initializeViews = () => {
      if (views && views.length > 0) {
        
        // Skip if we've already initialized views for this subpage
        // if (initializedSubPages.current.has(currentSubPageKey)) {
        //   return;
        // }

        const queryParams = new URLSearchParams(location.search);
        const _selectedView = queryParams.get("selectedView");
        let filteredViews;
        const enableBagelAI = subPage?.enableBagelAI;
        if(subPageKey){
          //update views if subpageKey not exist in view.subPageKeys
          const processedViews = views.map( (view: any) => {
            if (!view.subPageKey) {
              view.subPageKey = [];
            }
            if (view.isSubPageView && !view?.subPageKeys?.includes(subPageKey)) {
              view?.subPageKeys?.push(subPageKey);
              try {
                addViews({
                  _id: view._id,
                  subPageKey: subPageKey,
                  isGlobal: view.isGlobal,
                  isSubPageView: view.isSubPageView
                }, auth0);
              } catch (error) {
                console.error('Failed to update view subPageKey:', error);
              }
            }
            return view;
          })
          filteredViews = processedViews.filter((view: any) => 
            (enableBagelAI && view.name == 'Bagel AI') ||
            view.subPageKeys?.includes(subPageKey)
          );
          //add  subpage's defaultFilter  in each views's filters array
          filteredViews.forEach((view: any) => {
            if(!view.filters){
              view.filters = [];
            }
            if (subPage?.defaultFilters && Array.isArray(subPage.defaultFilters)) {
              const defaultFiltersWithKey = subPage.defaultFilters.map((filter: any) => ({
                ...filter,
                default: true
              }));
              // Only add default filters that don't already exist in view filters
              defaultFiltersWithKey.forEach((defaultFilter: any) => {
                const existingFilter = view.filters.find((f: any) => f.name === defaultFilter.name);
                if (!existingFilter) {
                  view.filters.push(defaultFilter);
                }
              });
            } else if (subPage?.defaultFilters) {
              const defaultFilterWithKey = {
                ...subPage.defaultFilters,
                default: true
              };
              // Only add default filter if it doesn't already exist in view filters
              const existingFilter = view.filters.find((f: any) => f.name === defaultFilterWithKey.name);
              if (!existingFilter) {
                view.filters.push(defaultFilterWithKey);
              }
            }
          });
        } else {
          filteredViews = views.filter((view: any) => 
            (view?.createdBy !== 'subpage') || (view?.createdBy == 'subpage' && view?.isSubPageView)
          );
          //add defaultFilter  in each views's filters array
          filteredViews.forEach((view: any) => {
            if (fields?.defaultFilters && Array.isArray(fields?.defaultFilters)) {
              const defaultFiltersWithKey = fields.defaultFilters.map((filter: any) => ({
                ...filter,
                default: true
              }));
              // Only add default filters that don't already exist in view filters
              defaultFiltersWithKey.forEach((defaultFilter: any) => {
                const existingFilter = view.filters.find((f: any) => f.name === defaultFilter.name);
                if (!existingFilter) {
                  view.filters.push(defaultFilter);
                }
              });
            } else if (fields?.defaultFilters) {
              const defaultFilterWithKey = {
                ...fields.defaultFilters,
                default: true
              };
              // Only add default filter if it doesn't already exist in view filters
              const existingFilter = view.filters.find((f: any) => f.name === defaultFilterWithKey.name);
              if (!existingFilter) {
                view.filters.push(defaultFilterWithKey);
              }
            }
          });
        }
        setSavedViews(filteredViews);        
        // Always set selected view when views change or subPageKey changes
        if (!_selectedView || filteredViews.length <= 1) {
          setSelectedView(filteredViews[0]);
        } else if(_selectedView){
          const selected = filteredViews.find((sv:any) => sv.name.toLowerCase() == _selectedView.toLowerCase())
          if(selected){
            setSelectedView(selected)
          }else{
            // View from URL doesn't exist in current subpage, clear URL parameter and select first view
            setSelectedView(filteredViews[0]);
            // Update URL to remove the invalid selectedView parameter
            // const newQueryParams = new URLSearchParams(location.search);
            // newQueryParams.delete('selectedView');
            // const newUrl = `${location.pathname}${newQueryParams.toString() ? '?' + newQueryParams.toString() : ''}`;
            // // Use setTimeout to avoid race condition with other URL updates
            // setTimeout(() => navigate(newUrl, { replace: true }), 0);
          }

        }
        // Mark this subpage as initialized
        // initializedSubPages.current.add(currentSubPageKey);
      }
    };

    initializeViews();
  }, [views, subPageKey, subPage]);

  // Effect to handle default sorting when ideaDefaultSortColumn changes or when sortStatus needs to be reset
  useEffect(() => {
    // Only apply default sorting if current sortStatus is empty
    if (!sortStatus?.columnAccessor || sortStatus.columnAccessor === "" || sortStatus.columnAccessor === "null") {
      // Check for subpage-specific default sorting first
      if (subPageKey) {
        if (subPage?.defaultSortColumn) {
          setSortStatus({ columnAccessor: subPage.defaultSortColumn, direction: "asc" });
          console.log('🎯 Applied subpage-specific default sort column:', subPage.defaultSortColumn);
          return;
        }
      }
      
      // Fallback to global default sorting
      if (ideaDefaultSortColumn) {
        setSortStatus({ columnAccessor: ideaDefaultSortColumn, direction: "asc" });
        console.log('🎯 Applied global default sort column:', ideaDefaultSortColumn);
      }
    }
  }, [ideaDefaultSortColumn, subPageKey, subPage, sortStatus]);

  useEffect(() => {
    setInputValue(subPageName)
    setEditMode(false)
  }, [subPageName])

  // useEffect(() => {
  //   // Clear initialized tracking when subPageKey changes
  //   initializedSubPages.current.clear();
  // }, [subPageKey]);

  useEffect(() => {
    setBgColor("#ffffff");
    setPageName("idea");
  }, [setBgColor]);

  const handleSaveViews = async () => {
    try {
      const res = await upsertView({ 
        name: viewName, 
        filters: activeFilters, 
        subPageKey: subPageKey, 
        isGlobal: userRole === "admin" ? isGlobal : false, 
        isSubPageView: userRole === "admin" ? isSubPageView : false 
      });
      setSelectedView({
        _id: res?.data?.updateOneResult?.upsertedId,
        name: viewName,
        filters: activeFilters,
      });
      setViewName("");
      showNotification({
        title: "Saved successfully!",
        color: "teal",
        message: "view saved successfully",
        icon: <IconCheck size="1.1rem" />,
        autoClose: 1200,
      });
      doClose();
    } catch (error) {
      console.error("Failed to save view:", error);
    }
  };

  const loadCanEditTeamIds = () => {
    canEditTeamIds(auth0)
      .then((customizations: any) => {
        const name =
          customizations && customizations.length > 0
            ? customizations[0].name
            : undefined;
        if (name === "committedDate") {
          setIsAllowedToEditDates(true);
        } else {
          setIsAllowedToEditDates(false);
        }
      })

      .catch((e) => {
        console.log(e);
      });
  };

  // Simplified effects - React Query handles most of the data fetching logic
  useEffect(() => {
    setMenuFiltersObj(ideasFiltersConfig);
  }, [ideasFiltersConfig]);

  useEffect(() => {
    if (debouncedSearch !== search) {
      setSortStatus({ columnAccessor: "none", direction: "asc" });
      console.log('🔍 Search changed, cache will update automatically');
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (selectedView?.filters) {
      const _filters = selectedView?.filters.map((filter: any) =>
        filter.type == "date" && filter.operator
          ? {
            ...filter,
            selected: getFixedDateRanges[filter.operator].values,
          }
          : {
            ...filter,
          }
      );
      let combined;
      if(isInitializing){ //combine only when back button is clicked
        combined = [..._filters, ...activeFilters];
        setIsInitializing(false);
      } else {
        combined = [..._filters];
      }
      
      const uniqueByName = [];

      const seen = new Set();
      for (const item of combined) {
        const name = item.name || item.parent;
        if (!seen.has(name?.toLowerCase()) && name) {
          seen.add(name.toLowerCase());
          uniqueByName.push({...item, parent: item.name || item.parent});
        }
      }
      setActiveFilters(uniqueByName);
      console.log('🎯 View filters changed, cache will update automatically');
    }
    setPage("1");
    setIsGlobal(selectedView?.isGlobal);
    setIsSubPageView(selectedView?.isSubPageView);
  }, [selectedView]);

  useEffect(() => {
    loadCanEditTeamIds();
    loadListMembers(setMenuFiltersObj, auth0);
    const queryParams = new URLSearchParams(location.search);
    const filtersStr = queryParams.get("filters");
    const filters = filtersStr
      ? JSON.parse(decodeURIComponent(filtersStr))
      : undefined;
    if (filters) {
      setActiveFilters(filters);
      setCheckedComponents((prev) => ({
        ...prev,
        ...filters.reduce(
          (acc: any, filter: any) => ({
            ...acc,
            [filter.parent.toLowerCase()]: filter?.selected?.map((i: string) =>
              i.toLowerCase()
            ),
          }),
          {}
        ),
      }));
    }
  }, []);

  useEffect(() => {
    const newQueryString = newBuildQueryString(
      page || "1",
      activeFilters,
      selectedView,
      JSON.stringify(sortStatus),
      ideasFiltersConfig
    );
    navigate(`${location.pathname}?${newQueryString}`, { replace: true });
  }, [
    page, 
    activeFilters, 
    sortStatus, 
    selectedView,
    location.pathname
  ]);

  // Clone handler
  const handleClone = async () => {
    const payload = {
      subPageKey: subPageKey
    };
    try {
      cloneSubpage(auth0, payload)
        .then((data: any) => {
          setSubPageKey(data?.subPageKey);
          
          navigate(`/ideas/${data?.subPageKey}`, { replace: true });
          window.location.reload();
        })
        .catch((error: any) => console.error('Failed to clone page:', error))
    } catch (err) {
      alert('Failed to clone page');
    }
  };

  const tableColoumns = useMemo(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔧 Computing table columns:', {
        ideaColumns: ideaColumns?.length || 0,
        subPageKey,
        selectedViewName: selectedView?.name,
        hasSubPage: subPage
      });
    }
    if(ideaColumns){
      let mergedColumns = ideaColumns;
      if(subPage){
        //function for over writing the columns based on the subPageName
        mergedColumns = getMergedColumns(subPageName, ideaColumns, subPage);
      }
      if (selectedView?.name === 'Bagel AI') {
  
        const relevantColoumns = mergedColumns.filter((column: any) => column.generatedEnabled !== false && column.accessor !== '_id')
        return relevantColoumns
      }
      else {
        return mergedColumns
      }
    }
  }, [ideaColumns, subPageKey, selectedView?.name, subPage]);

  const handleEdit = () => { 
    setEditMode(true)
    setEditTitle(true)
  };

  const handleDelete = async () => {
    try {
      // Call the subpage PUT API to delete the page
      await deleteSubpage(auth0, { 
        subPageKey: subPageKey,
        deleted: true 
      });
      
      // Show success notification
      showNotification({
        title: "Page deleted successfully",
        color: "teal",
        message: "The page has been deleted",
        icon: <IconCheck size="1.1rem" />,
        autoClose: 2000
      });
      
      // Redirect to /ideas
      navigate('/ideas', { replace: true });
      
    } catch (error) {      
      // Show error notification
      showNotification({
        title: "Error",
        color: "red",
        message: "Failed to delete page. Please try again.",
        icon: <IconX size="1.1rem" />,
        autoClose: 3000
      });
    }
  };

  const handleNameSave = async () => {
    if(inputValue === subPageName) {
      return;
    }
    setInputValue(inputValue);
    editSubpage(auth0, { title: inputValue, subPageKey: subPageKey})
      .then((data) =>{
        setSubPageKey(data?.subPageKey);
        navigate(`/ideas/${data?.subPageKey}`, { replace: true });
        window.location.reload();
      }).catch((error) => console.error('Failed to edit subpage:', error))
  };

  const openDeleteModal = () =>{
    openConfirmModal({
      title: <Text fz={20} fw={600} ml={20} mt={40}>Delete page</Text>,
      size: 496,
      centered: true,
      children: (
        <Text size="sm" mt={15} mb={30}>Are you sure you want to delete this page?</Text>
      ),
      styles: {
        close: {
          alignSelf: "flex-start",  
          marginTop: 10,
          marginRight: 10,
        },
        header:{
          padding: "10px 20px"
        },
        body: {
          height: 120, 
          padding: 40,
        },
        
      },
      labels: { confirm: "Delete page", cancel: "No, keep it" },
      confirmProps: {
        color: "red",
        px: 8,
        styles: {
          label: {
            fontSize: 12, 
            fontWeight: 500
          },
        },
        leftIcon: <IconTrash size={14} />,
      },
      cancelProps: {
        leftIcon: <IconCheck size={16} />,
        px: 8,
        styles: {
          label: {
            fontSize: 12, 
            fontWeight: 500
          },
        },
      },
      onCancel: () => console.log("Cancel"),
      onConfirm: () => handleDelete()
    });

  }

  return (
    <Box>
      <Box bg={"#fff"} ref={ref}>
        <Box pt={20} mx={20}>
          <Flex align="center" justify="space-between">
            <Flex justify="space-between" style={{ width: '100%' }}>
              {/* Editable Page Name */}
              {editMode && userRole === "admin"? (
                <>
                  {editMode && editTitle && subPageKey ? (
                    <Flex align="left">
                      <Tooltip label="Edit name">
                        <TextInput
                          value={inputValue}
                          onChange={e => setInputValue(e.currentTarget.value)}
                          onBlur={handleNameSave}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleNameSave();
                          }}
                          size="sm"
                          styles={{ input: { fontSize: 20, fontWeight: 600, marginBottom: 13, width: 250 } }}
                          autoFocus
                        />
                      </Tooltip>
                    </Flex> ) : (
                    <Flex align="center">
                      <Text fz="20px"  fw={600}  mb={13}  >
                        {isArchived ? "Ideas - Archived" : inputValue || "Ideas"}
                      </Text>
                    </Flex>
                  )}

                  <Flex align="center">
                    <Button
                      size="xs"
                      // variant="outline"
                      sx = {{marginBottom: 13, fontWeight: 500, color: "white", backgroundColor: "#fa5252", border: '0.0625rem solid #fa5252'}}
                      ml="md"
                      styles={(theme) => ({
                        root: {
                          marginBottom: '13px',
                          width:'140px',
                          height: "31.8px",
                          backgroundColor: "#fa5252",
                          border: 0,
                          fontWeight: 400,
                          padding: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          position: "relative", 
                          "&:hover": {
                            backgroundColor: theme.fn.darken("#fa5252", 0.05),
                          },
                        },
              
                        loader: {
                          position: "absolute", 
                          color: theme.white,
                        }
                      })}
                      onClick={() => setEditModalOpen(true)}
                    >
                    Edit table view
                    </Button>
                    {editMode && (
                      <Badge
                        color="red"
                        variant="light"
                        size="lg"
                        mr="md" 
                        style={{ marginBottom: 13, marginLeft: 10 }}
                        rightSection={
                          <ActionIcon
                            variant="subtle"
                            color="grey"
                            onClick={() => setEditMode(false)}
                            size="xs"
                            title="Cancel edit mode"
                            style={{ marginLeft: 8 }}
                          >
                            <IconX size={12} color="grey" />
                          </ActionIcon>
                        }
                      >
                        Edit mode
                      </Badge>
                    )}
                  </Flex>
                </>
              ) : (
                <Flex align="center">
                  <Text
                    fz="20px"
                    fw={600}
                    mb={13}
                  >
                    {isArchived ? "Ideas - Archived" : inputValue || "Ideas"}
                  </Text>
                </Flex>
              )}
            </Flex>

            {/* Settings Menu */}
            { !editMode && userRole === "admin" && (
              <Menu shadow="md" width={180} position="bottom-end" withinPortal={false}>
                <Menu.Target>
                  <Tooltip label="Edit" withArrow>
                    <span>
                      <IconSettings style={{ cursor: 'pointer', color: 'grey' }} />
                    </span>
                  </Tooltip>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    icon={<IconCopy size={16} />}
                    onClick={handleClone}
                  >
                    Duplicate Page
                  </Menu.Item>
                  <Menu.Item
                    icon={<IconSettingsMenu size={16} />}
                    onClick={handleEdit}
                  >
                    Edit Page
                  </Menu.Item>
                  <Menu.Item
                    icon={<IconTrash size={16} />}
                    onClick={openDeleteModal}
                    disabled={!subPageKey}
                  >
                    Delete Page
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Flex>
          {/* Header */}
          <Box ref={warpRef}>
            <Header
              savedViews={savedViews}
              selectedView={selectedView}
              setSelectedView={setSelectedView}
              upsertView={upsertView}
              warpWidth={warpWidth}
              isGlobal={isGlobal}
              setIsGlobal={setIsGlobal}
              isSubPageView={isSubPageView}
              setIsSubPageView={setIsSubPageView}
              actionIconsWidth={actionIconsWidth}
              isArchived={isArchived}
            />
          </Box>
          {/* Filter Actions */}
          {filterRowOpened && (
            <FilterActions
              activeFilters={activeFilters}
              setActiveFilters={setActiveFilters}
              selectedView={selectedView}
              open={open}
              handleSaveViews={handleSaveViews}
              opened={opened}
              viewName={viewName}
              setViewName={setViewName}
              isGlobal={isGlobal}
              setIsGlobal={setIsGlobal}
              isSubPageView={isSubPageView}
              setIsSubPageView={setIsSubPageView}
              doClose={doClose}
              toggle={toggle}
              search={search}
              setSearch={setSearch}
              searchOpened={searchOpened}
              setSearchOpened={setSearchOpened}
              actionIconsRef={actionIconsRef}
              sortState={sortState}
              setSortState={setSortState}
            />
          )}

          {/* Main Table Display */}
          <Box>
            {tableColoumns && tableColoumns.length > 0 ? (
              <IdeasTable
                ideaColumns={tableColoumns}
                ideas={ideas}
                isAllowedToEditDates={isAllowedToEditDates}
                handlePageChange={(newPage: string) => setPage(newPage)}
                totalRecords={totalCount}
                recordsPerPage={PAGE_SIZE}
                page={Number(page) || 1}
                sortStatus={sortStatus}
                setSortStatus={setSortStatus}
                loading={loading}
                PAGE_SIZE={PAGE_SIZE}
                selectedView={selectedView}
                isTagsHovered={isTagsHovered}
                sortState={sortState}
              />
            ) : (
              <Stack h="66vh" w="88vw" align="center" justify="center">
                <Loader />
                {/* Debug info */}
                {process.env.NODE_ENV === 'development' && (
                  <Text size="sm" color="dimmed">
                    Columns: {tableColoumns?.length || 0}, IdeaColumns: {ideaColumns?.length || 0}
                  </Text>
                )}
              </Stack>
            )}
          </Box>
        </Box>
      </Box>
      {editMode && (
        <EditTableViewModal opened={editModalOpen} onClose={() => setEditModalOpen(false)} subPageKey={subPageKey} subPage={subPage} setEditMode={setEditMode}/>
      )}
    </Box>
  );
};

export const IdeasWraper = ({ pageKey }: { pageKey: any }) => {
  return (
    <FiltersContextProvider initialSubpage={pageKey}>
      <Ideas />
    </FiltersContextProvider>
  );
};
